import type { Item, Source, DigestPreview, Metrics } from './types.ts';

const BASE = (import.meta.env.VITE_API_BASE ?? 'https://odis-api.dcochrane-39a.workers.dev') + '/api';

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText })) as { error: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  items: {
    list: (params?: { status?: string; category?: string; source_id?: string; score_min?: number; limit?: number; offset?: number; q?: string }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.category) qs.set('category', params.category);
      if (params?.source_id) qs.set('source_id', params.source_id);
      if (params?.score_min) qs.set('score_min', String(params.score_min));
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      if (params?.q) qs.set('q', params.q);
      return apiFetch<{ items: Item[]; total: number; limit: number; offset: number }>(`/items?${qs}`);
    },
    get: (id: string) => apiFetch<Item>(`/items/${id}`),
    setStatus: (id: string, status: string, dismissedReason?: string) =>
      apiFetch<{ id: string; status: string }>(`/items/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status, dismissed_reason: dismissedReason }),
      }),
  },
  sources: {
    list: () => apiFetch<{ sources: Source[] }>('/sources'),
    update: (id: string, patch: { active?: boolean; cron_schedule?: string }) =>
      apiFetch(`/sources/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  },
  digest: {
    preview: () => apiFetch<DigestPreview>('/digest/preview'),
    send: () => apiFetch<{ ok: boolean; message: string }>('/digest/send', { method: 'POST' }),
  },
  metrics: () => apiFetch<Metrics>('/metrics'),
  chart: (id: string) => apiFetch<{ item_id: string; title: string; chart_spec: unknown }>(`/chart/${id}`),
  health: () => apiFetch<{
    status: 'ok' | 'degraded';
    checked_at: number;
    sources: { total_active: number; unhealthy: { id: string; name: string; error_count: number }[] };
    ai_cost_today: { total_calls: number; total_cost_usd: number; haiku_calls: number; sonnet_calls: number };
  }>('/health'),
  aiCosts: (days?: number) => apiFetch<{ days: number; rows: { date: string; model: string; calls: number; cost_usd: number }[] }>(
    `/ai-costs${days ? `?days=${days}` : ''}`
  ),
};
